import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Mail, Lock, UserRound, ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/project/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useProjectLocal } from "@/context/AppContext";
import { apiPost } from "@/api/client";
import { normalizeEmail, validateAuthForm } from "@/lib/validation";

const config = {
  signup: ["Create your account", "Start onboarding for your local business — guided setup at your pace."],
  login: ["Welcome back", "Open the owner dashboard for your restaurant."],
  forgot: ["Reset your password", "We’ll send a reset link when email delivery is connected."],
  reset: ["Choose a new password", "Complete your password reset."],
};

export function AuthPage({ mode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup, completeMfaLogin } = useProjectLocal();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [mfa, setMfa] = useState({ required: false, token: "", code: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [title, subtitle] = config[mode];

  const finishLogin = (result) => {
    if (result.user?.role === "platform_admin") {
      window.location.assign("/admin");
    } else if (result.business && !result.business.onboarding_completed) {
      const step = result.business.subscription_status !== "active" ? "subscription" : "publish";
      navigate(`/onboarding/${step}`, { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const check = validateAuthForm(
      mode,
      { ...form, code: mfa.code },
      { mfa: mfa.required },
    );
    if (!check.ok) {
      setErrors({ [check.field]: check.message });
      toast.error(check.message);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await signup(form.name.trim(), normalizeEmail(form.email), form.password);
        navigate("/onboarding/business-type");
      } else if (mode === "login") {
        if (mfa.required) {
          const result = await completeMfaLogin(mfa.token, mfa.code.trim());
          finishLogin(result);
          return;
        }
        const result = await login(normalizeEmail(form.email), form.password);
        if (result.mfa_required) {
          setMfa({ required: true, token: result.mfa_token, code: "" });
          toast.message("Enter the code from your authenticator app");
          return;
        }
        finishLogin(result);
      } else if (mode === "forgot") {
        await apiPost("/auth/forgot-password", { email: normalizeEmail(form.email) });
        toast.success("Password reset request received");
        navigate("/login");
      } else if (mode === "reset") {
        const token = String(searchParams.get("token") || "").trim();
        if (!token) {
          toast.error("Reset link is missing or invalid. Request a new reset email.");
          return;
        }
        await apiPost("/auth/reset-password", {
          token,
          new_password: form.password,
        });
        toast.success("Password reset complete. Log in with your new password.");
        navigate("/login");
      } else {
        toast.error("Open the reset link from your email to set a new password.");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        detail?.error?.message
        || (typeof detail === "string" ? detail : null)
        || detail?.message
        || error.message
        || "Authentication failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell grain grid min-h-screen place-items-center px-3 py-6 sm:px-4 sm:py-10" data-testid={`${mode}-auth-page`}>
      <section className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-2xl border bg-white shadow-2xl sm:rounded-[28px] md:grid-cols-[.9fr_1.1fr]" data-testid={`${mode}-auth-panel`}>
        <div className="hidden bg-[#173B2F] p-8 text-white md:block" data-testid={`${mode}-auth-side`}>
          <LogoMark light />
          <h1 className="mt-16 text-4xl font-extrabold" data-testid={`${mode}-auth-side-title`}>Launch a polished local storefront in one guided flow.</h1>
          <p className="mt-4 text-white/76" data-testid={`${mode}-auth-side-body`}>Set up your storefront, products, ordering, and daily operations in one guided workspace.</p>
        </div>
        <form onSubmit={submit} className="p-5 sm:p-10" data-testid={`${mode}-auth-form`} autoComplete="off" noValidate>
          <div className="md:hidden"><LogoMark /></div>
          <h2 className="mt-6 text-2xl font-extrabold sm:mt-8 sm:text-3xl md:mt-0" data-testid={`${mode}-auth-title`}>
            {mfa.required ? "Two-factor authentication" : title}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground" data-testid={`${mode}-auth-subtitle`}>
            {mfa.required ? "Enter the 6-digit code from your authenticator app (or a backup code)." : subtitle}
          </p>
          <div className="mt-8 space-y-5">
            {mfa.required ? (
              <Field
                icon={<ShieldCheck />}
                label="Authenticator code"
                type="text"
                testId={`${mode}-mfa-code-input`}
                value={mfa.code}
                onChange={(value) => setMfa({ ...mfa, code: value })}
                autoComplete="one-time-code"
                error={errors.code}
              />
            ) : (
              <>
                {mode === "signup" && (
                  <Field
                    icon={<UserRound />}
                    label="Full name"
                    type="text"
                    testId={`${mode}-full-name-input`}
                    value={form.name}
                    onChange={(value) => setForm({ ...form, name: value })}
                    autoComplete="off"
                    error={errors.name}
                  />
                )}
                {mode !== "reset" && (
                  <Field
                    icon={<Mail />}
                    label="Email"
                    type="email"
                    testId={`${mode}-email-input`}
                    value={form.email}
                    onChange={(value) => setForm({ ...form, email: value })}
                    autoComplete="off"
                    error={errors.email}
                    inputMode="email"
                  />
                )}
                {mode !== "forgot" && (
                  <Field
                    icon={<Lock />}
                    label="Password"
                    type="password"
                    testId={`${mode}-password-input`}
                    value={form.password}
                    onChange={(value) => setForm({ ...form, password: value })}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    error={errors.password}
                    hint={mode === "signup" ? "At least 8 characters." : undefined}
                  />
                )}
                {(mode === "signup" || mode === "reset") && (
                  <Field
                    icon={<Lock />}
                    label="Confirm password"
                    type="password"
                    testId={`${mode}-password-confirm-input`}
                    value={form.confirmPassword}
                    onChange={(value) => setForm({ ...form, confirmPassword: value })}
                    autoComplete="new-password"
                    error={errors.confirmPassword}
                  />
                )}
              </>
            )}
          </div>
          <Button data-testid={`${mode}-submit-button`} className="tap-target mt-8 w-full rounded-lg font-bold" type="submit" disabled={submitting}>
            {submitting
              ? "Please wait…"
              : mfa.required
                ? "Verify and continue"
                : mode === "forgot"
                  ? "Send reset link"
                  : mode === "reset"
                    ? "Save password"
                    : mode === "login"
                      ? "Log in"
                      : "Continue to onboarding"}
          </Button>
          <div className="mt-6 flex flex-wrap gap-3 text-sm" data-testid={`${mode}-auth-links`}>
            <Link data-testid={`${mode}-login-link`} className="font-bold text-primary" to="/login">Log in</Link>
            <Link data-testid={`${mode}-signup-link`} className="font-bold text-primary" to="/signup">Sign up</Link>
            <Link data-testid={`${mode}-forgot-link`} className="font-bold text-primary" to="/forgot-password">Forgot password</Link>
          </div>
        </form>
      </section>
    </main>
  );
}

function Field({ label, icon, type, testId, value, onChange, autoComplete = "off", error, hint, inputMode }) {
  const isPassword = type === "password";
  return (
    <div>
      <Label className="text-sm font-bold" data-testid={`${testId}-label`} htmlFor={testId}>{label}</Label>
      <div className="relative mt-2">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>
        {isPassword ? (
          <PasswordInput
            id={testId}
            data-testid={testId}
            className={`h-12 rounded-lg pl-10 ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            autoComplete={autoComplete}
            aria-invalid={Boolean(error)}
            minLength={8}
            maxLength={128}
          />
        ) : (
          <Input
            id={testId}
            data-testid={testId}
            className={`h-12 rounded-lg pl-10 ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
            type={type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            autoComplete={autoComplete}
            inputMode={inputMode}
            aria-invalid={Boolean(error)}
            maxLength={type === "email" ? 254 : 80}
          />
        )}
      </div>
      {error ? (
        <p className="mt-1.5 text-xs font-semibold text-red-600" data-testid={`${testId}-error`}>{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`${testId}-hint`}>{hint}</p>
      ) : null}
    </div>
  );
}
